/**
 * wall-art-format-video.js
 *
 * Drives the per-format video swap for the product gallery's slot 5
 * (the static-detail slot). When the customer changes format in the
 * wall-art picker, the slide's <video> is swapped to the matching
 * format's uploaded video — or the slot + thumbnail are hidden when
 * the picked format has no video uploaded.
 *
 * Data source: a sibling
 *   <script type="application/json" id="FormatVideoData-<section.id>">
 *     [
 *       { "format": "art_print", "video_html": "<video ...>",
 *         "poster_url": "...", "alt": "..." },
 *       ...
 *     ]
 *   </script>
 * (emitted by snippets/product-media-gallery.liquid for each format
 * that has a video uploaded — empty array / missing tag = no videos.)
 *
 * Trigger: `wallart:selection:changed` events from
 * assets/wall-art-picker.js, with
 * `detail = { format, size, frameColor, variantId, price }`.
 *
 * Swap algorithm (per event):
 *   1. Look up entry by detail.format.
 *   2. If no entry → set `data-no-video` on slot 5 main slide + its
 *      thumbnail <li>s (CSS hides). If slot 5 was active, click the
 *      first non-slot-5 thumbnail to jump away.
 *   3. If entry →
 *      a. Clear `data-no-video` on both <li>s.
 *      b. window.pauseAllMedia() — Dawn global (assets/global.js).
 *      c. Remove any appended <video>/<iframe>/<model-viewer> direct
 *         child of <deferred-media> (Dawn's loadContent appends one).
 *      d. Replace <template>.innerHTML with entry.video_html.
 *      e. Clear `loaded` attribute on <deferred-media> (defeats the
 *         one-shot cache so loadContent runs again on next activation).
 *      f. Swap poster <img data-format-video-poster> src/alt.
 *      g. Swap thumbnail <img data-format-video-thumbnail> src/alt.
 *      h. If slot 5 is currently `.is-active`, call
 *         deferredMedia.loadContent(false) immediately so the new
 *         video renders in place without a second click. Customer
 *         still hits the video's own play button to actually play.
 *
 * Lifecycle: initial pass plus shopify:section:load (variant AJAX
 * re-renders the product-info section). Each controller carries an
 * AbortController so shopify:section:unload cleanly detaches listeners.
 *
 * Why one <deferred-media> + template hot-swap (not 4 sibling
 * deferred-medias): Dawn's media-gallery.js calls
 *   playActiveMedia(activeItem) {
 *     window.pauseAllMedia();
 *     const deferredMedia = activeItem.querySelector('.deferred-media');
 *     if (deferredMedia) deferredMedia.loadContent(false);
 *   }
 * with a SINGULAR querySelector — only the first <deferred-media>
 * child in DOM order would ever get loadContent invoked. A toggle-
 * hidden sibling design would silently break for 3 of the 4 formats.
 */

(function () {
  'use strict';

  const SLOT5_SLIDE_SELECTOR = '[data-format-video-slide]';
  const SLOT5_THUMB_SELECTOR = '[data-format-video-thumbnail-slide]';
  const CONTAINER_SELECTOR   = '[data-format-video-container]';
  const TEMPLATE_SELECTOR    = '[data-format-video-template]';
  const POSTER_IMG_SELECTOR  = '[data-format-video-poster]';
  const THUMB_IMG_SELECTOR   = '[data-format-video-thumbnail]';
  const DATA_SCRIPT_SELECTOR = 'script[type="application/json"][id^="FormatVideoData-"]';

  // Per-section controllers, so we can tear them down on shopify:section:unload.
  // Key: section id (e.g. "template--12345__main"). Value: AbortController.
  const controllers = new Map();

  function readFormatVideoData(sectionRoot) {
    const tag = sectionRoot.querySelector
      ? sectionRoot.querySelector(DATA_SCRIPT_SELECTOR)
      : null;
    if (!tag) return [];
    try {
      const parsed = JSON.parse(tag.textContent || 'null');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function findEntry(data, format) {
    if (!Array.isArray(data) || !format) return null;
    for (const e of data) {
      if (!e) continue;
      if (e.format === format) return e;
    }
    return null;
  }

  /** Walk up from the gallery to the per-section root that carries
   *  the picker, the gallery, the data script, etc. */
  function findSectionRootForGallery(gallery) {
    return (
      gallery.closest('product-info') ||
      gallery.closest('[id^="ProductInfo-"]') ||
      gallery.closest('[id^="MainProduct-"]') ||
      document
    );
  }

  function setHidden(slide, thumb, hidden) {
    if (slide) {
      if (hidden) slide.setAttribute('data-no-video', '');
      else slide.removeAttribute('data-no-video');
    }
    if (thumb) {
      if (hidden) thumb.setAttribute('data-no-video', '');
      else thumb.removeAttribute('data-no-video');
    }
  }

  /** Find the first thumbnail that isn't our slot-5 and synthesize
   *  a click on it. media-gallery.js handles the rest (deactivates
   *  slot 5, activates target slide, plays its media). */
  function jumpAwayFromSlot5(thumbsContainer) {
    if (!thumbsContainer) return;
    const items = thumbsContainer.querySelectorAll('[data-target]');
    for (const item of items) {
      if (item.matches(SLOT5_THUMB_SELECTOR)) continue;
      const btn = item.querySelector('button');
      if (btn) {
        btn.click();
        return;
      }
    }
  }

  function applySwap(sectionRoot, formatVideoData, detail) {
    if (!detail) return;
    const gallery = sectionRoot.querySelector
      ? sectionRoot.querySelector('media-gallery')
      : null;
    if (!gallery) return;

    const slide = gallery.querySelector(SLOT5_SLIDE_SELECTOR);
    const thumbsContainer = gallery.querySelector('[id^="GalleryThumbnails-"]');
    const thumb = thumbsContainer
      ? thumbsContainer.querySelector(SLOT5_THUMB_SELECTOR)
      : null;
    const entry = findEntry(formatVideoData, detail.format);
    const slot5IsActive = !!(slide && slide.classList.contains('is-active'));

    if (!entry) {
      setHidden(slide, thumb, true);
      if (slot5IsActive) jumpAwayFromSlot5(thumbsContainer);
      return;
    }

    setHidden(slide, thumb, false);

    const container = slide ? slide.querySelector(CONTAINER_SELECTOR) : null;
    const template  = container ? container.querySelector(TEMPLATE_SELECTOR) : null;
    const poster    = container ? container.querySelector(POSTER_IMG_SELECTOR) : null;
    const thumbImg  = thumb ? thumb.querySelector(THUMB_IMG_SELECTOR) : null;
    if (!container || !template) return;

    // Pause anything currently playing site-wide before we mutate.
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

    // Remove any appended <video>/<iframe>/<model-viewer> from a
    // prior loadContent() call. Dawn's global.js appends ONE direct
    // child of <deferred-media> per load — we drop it so the next
    // loadContent appends a fresh one from our updated template.
    for (const child of Array.from(container.children)) {
      const tag = child.tagName;
      if (tag === 'VIDEO' || tag === 'IFRAME' || tag === 'MODEL-VIEWER') {
        child.remove();
      }
    }

    // Swap the template body. Setting innerHTML on <template> updates
    // its .content DocumentFragment per the HTML spec, which is what
    // global.js's loadContent reads via .cloneNode(true).
    template.innerHTML = entry.video_html || '';

    // Clear the one-shot cache attribute so loadContent runs again.
    container.removeAttribute('loaded');

    if (poster) {
      if (entry.poster_url) poster.setAttribute('src', entry.poster_url);
      if (entry.alt) poster.setAttribute('alt', entry.alt);
      poster.removeAttribute('srcset');
    }
    if (thumbImg) {
      if (entry.poster_url) thumbImg.setAttribute('src', entry.poster_url);
      if (entry.alt) thumbImg.setAttribute('alt', entry.alt);
      thumbImg.removeAttribute('srcset');
    }

    // If slot 5 is currently the active slide, fire loadContent now
    // so the new video renders in place without the customer clicking
    // anywhere.
    if (slot5IsActive && typeof container.loadContent === 'function') {
      container.loadContent(false);
    }
  }

  function initForGallery(gallery) {
    const sectionRoot = findSectionRootForGallery(gallery);
    const sectionId = String(gallery.id || '').replace(/^MediaGallery-/, '');
    if (!sectionId) return;

    // If a prior controller exists for this section, abort it first.
    // shopify:section:load can fire without an explicit unload first.
    if (controllers.has(sectionId)) {
      try { controllers.get(sectionId).abort(); } catch { /* ignore */ }
      controllers.delete(sectionId);
    }

    const formatVideoData = readFormatVideoData(sectionRoot);

    const abortController = new AbortController();
    controllers.set(sectionId, abortController);

    const onChange = (event) => {
      applySwap(sectionRoot, formatVideoData, event && event.detail);
    };

    // Dual-binding: the picker dispatches on its own root which
    // bubbles to document, but we ALSO bind on the section root so
    // we don't miss events captured before they hit document (and
    // so that two product-info sections on the same page — quick-add
    // modal context — don't cross-talk).
    sectionRoot.addEventListener('wallart:selection:changed', onChange, {
      signal: abortController.signal,
    });
    document.addEventListener('wallart:selection:changed', onChange, {
      signal: abortController.signal,
    });

    // Seed from any pre-existing selection (the picker may have
    // dispatched before this listener bound — both scripts load
    // with `defer`).
    if (window.__wallArtCurrentSelection) {
      applySwap(sectionRoot, formatVideoData, window.__wallArtCurrentSelection);
    }
  }

  function initAll(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    // If the event target IS a media-gallery, also include it directly.
    if (root && root.matches && root.matches('media-gallery')) {
      initForGallery(root);
    }
    const galleries = scope.querySelectorAll('media-gallery');
    galleries.forEach((g) => initForGallery(g));
  }

  function destroySection(sectionId) {
    const ctrl = controllers.get(sectionId);
    if (!ctrl) return;
    try { ctrl.abort(); } catch { /* ignore */ }
    controllers.delete(sectionId);
  }

  function start() {
    initAll(document);

    document.addEventListener('shopify:section:load', (event) => {
      const target = event && event.target instanceof Element ? event.target : document;
      initAll(target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const detailSectionId = String(
        (event && event.detail && event.detail.sectionId) || ''
      ).trim();
      if (detailSectionId) {
        destroySection(detailSectionId);
        return;
      }
      const target = event && event.target instanceof Element ? event.target : null;
      if (!target) return;
      const gallery = (target.matches && target.matches('media-gallery'))
        ? target
        : (target.querySelector && target.querySelector('media-gallery'));
      if (!gallery) return;
      const derived = String(gallery.id || '').replace(/^MediaGallery-/, '');
      if (derived) destroySection(derived);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
